import React from 'react';
import { Form, Row, Col, Divider, Checkbox, Radio, DatePicker } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { 
  StyledInput, 
  StyledSelect, 
  SectionTitle,
  FormSection,
  StyledIcon,
  ZipCodeInput,
  DividerText
} from '../styled';
import { US_STATES } from '../utils/constants';
import { RACE_OPTIONS, RACE_SUBGROUPS, hasRaceSubgroups } from '../../../../constants/raceEthnicity';
import { AI_VOICE_CALL_CONSENT_TEXT } from '../../../../components/BusDataFormWizard/constants';

// Medical conditions that match the bus screening form
const busScreeningMedicalConditions = [
  { value: 'cancer', label: 'Cancer' },
  { value: 'heartProblems', label: 'Heart Problems' },
  { value: 'diabetes', label: 'Diabetes/Sugar' },
  { value: 'mentalHealthDisorders', label: 'Mental Health Disorders (e.g., bipolar, depression, anxiety, etc.)' },
  { value: 'obesity', label: 'Obesity' },
  { value: 'highBloodPressure', label: 'High Blood Pressure' },
  { value: 'badCholesterol', label: 'Bad Cholesterol' },
  { value: 'liverDisease', label: 'Liver Disease' },
  { value: 'kidneyDisease', label: 'Kidney Disease' },
  { value: 'stress', label: 'Stress' },
  { value: 'none', label: 'None of the above' }
];

const { Option } = StyledSelect;

const PatientInfoTab = () => {
  const form = Form.useFormInstance();
  return (
    <>
      <FormSection>
        <SectionTitle>
          <UserOutlined />
          Personal Information
        </SectionTitle>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <StyledInput 
                placeholder="Enter first name"
                prefix={<StyledIcon><UserOutlined /></StyledIcon>}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <StyledInput 
                placeholder="Enter last name"
                prefix={<StyledIcon><UserOutlined /></StyledIcon>}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Email Address"
              name="emailAddress"
              rules={[
                { required: true, message: 'Please enter email address' },
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
            >
              <StyledInput 
                placeholder="Enter email address"
                prefix={<StyledIcon><MailOutlined /></StyledIcon>}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Phone Number"
              name="phoneNumber"
            >
              <StyledInput 
                placeholder="(XXX) XXX-XXXX"
                prefix={<StyledIcon><PhoneOutlined /></StyledIcon>}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Date of Birth"
              name="dateOfBirth"
              rules={[{ required: true, message: 'Please select date of birth' }]}
            >
              <DatePicker 
                format="MM/DD/YYYY"
                placeholder="Select date of birth"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Birth Gender"
              name="birthGender"
            >
              <StyledSelect placeholder="Select birth gender">
                <Option value="male">Male</Option>
                <Option value="female">Female</Option>
              </StyledSelect>
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

        <Divider>
          <DividerText>Demographics</DividerText>
        </Divider>

      <FormSection>
        <Row gutter={[24, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Race/Ethnicity"
              name="raceEthnicity"
            >
              <StyledSelect
                placeholder="Select race/ethnicity"
                onChange={() => {
                  // Reset the dependent subgroup whenever top-level changes
                  form?.setFieldValue?.('raceEthnicitySubgroup', undefined);
                }}
              >
                {RACE_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </StyledSelect>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.raceEthnicity !== curr.raceEthnicity}
            >
              {({ getFieldValue }) => {
                const race = getFieldValue('raceEthnicity');
                if (!hasRaceSubgroups(race)) return null;
                return (
                  <Form.Item
                    label="Which best describes you? (optional)"
                    name="raceEthnicitySubgroup"
                  >
                    <StyledSelect placeholder="Select a more specific group" allowClear>
                      {RACE_SUBGROUPS[race].map((sub) => (
                        <Option key={sub.value} value={sub.value}>{sub.label}</Option>
                      ))}
                    </StyledSelect>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Annual Household Income"
              name="householdIncome"
            >
              <StyledSelect placeholder="Select income range">
                {/* New income ranges (current) */}
                <Option value="Less than $15,000">Less than $15,000</Option>
                <Option value="$15,000 - $24,999">$15,000 – $24,999</Option>
                <Option value="$25,000 - $34,999">$25,000 – $34,999</Option>
                <Option value="$35,000 - $49,999">$35,000 – $49,999</Option>
                <Option value="$50,000 - $74,999">$50,000 – $74,999</Option>
                <Option value="$75,000 - $99,999">$75,000 – $99,999</Option>
                <Option value="$100,000 or more">$100,000 or more</Option>
                <Option value="Prefer not to say">Prefer not to say</Option>
                
                {/* Legacy income ranges (for backwards compatibility) */}
                <Option value="0-26700">$0-$26,700 (Legacy)</Option>
                <Option value="26701-44500">$26,701-$44,500 (Legacy)</Option>
                <Option value="44501-71200">$44,501-$71,200 (Legacy)</Option>
                <Option value="over71200">Over $71,200 (Legacy)</Option>
                <Option value="preferNotToAnswer">Prefer not to answer (Legacy)</Option>
              </StyledSelect>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Household Size"
              name="householdSize"
            >
              <StyledSelect placeholder="Select household size">
                <Option value="1">1</Option>
                <Option value="2">2</Option>
                <Option value="3">3</Option>
                <Option value="4">4</Option>
                <Option value="5">5</Option>
                <Option value="6">6</Option>
                <Option value="7">7</Option>
                <Option value="8">8</Option>
                <Option value="9">9</Option>
                <Option value="10+">10+</Option>
              </StyledSelect>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Insurance Coverage"
              name="insuranceCoverage"
            >
              <StyledSelect placeholder="Select insurance coverage">
                <Option value="private">Private</Option>
                <Option value="insuredThroughEmployer">Insured through employer</Option>
                <Option value="medicaid">Medicaid</Option>
                <Option value="medicare">Medicare</Option>
                <Option value="military">Military</Option>
                <Option value="noCoverage">No coverage</Option>
              </StyledSelect>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="State of Residence"
              name="stateOfResidence"
            >
              <StyledSelect 
                placeholder="Select state"
                showSearch
                optionFilterProp="children"
              >
                {US_STATES.map((state) => (
                  <Option key={state.value} value={state.value}>
                    {state.label}
                  </Option>
                ))}
              </StyledSelect>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Event Zip Code"
              name="eventZip"
            >
              <ZipCodeInput 
                placeholder="Enter zip code"
                maxLength={5}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

        <Divider>
          <DividerText>Medical History</DividerText>
        </Divider>

      <FormSection>
        <Form.Item
          label="Ever suffered a heart attack or stroke?"
          name="heartAttackOrStroke"
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Checkbox value="heartAttack">Heart Attack</Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="stroke">Stroke</Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="none">None</Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Form.Item>

        <Form.Item
          label="Please rate your normal stress level"
          name="stressLevel"
        >
          <Radio.Group>
            <Radio value="1">1</Radio>
            <Radio value="2">2</Radio>
            <Radio value="3">3</Radio>
            <Radio value="4">4</Radio>
            <Radio value="5">5</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Would you be interested in our free mental health services?"
          name="mentalHealthInterest"
        >
          <Radio.Group>
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Medical Conditions"
          name="medicalConditions"
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
              {busScreeningMedicalConditions.map((option) => (
                <Col xs={24} sm={12} key={option.value}>
                  <Checkbox value={option.value}>{option.label}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>

        <Form.Item
          label="Have you ever seen/been referred to a cardiologist/heart doctor since being diagnosed with any of the above conditions?"
          name="referredToCardiologist"
        >
          <Radio.Group>
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Are you a parent or caregiver?"
          name="isParentOrCaregiver"
        >
          <Radio.Group>
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
            <Radio value="prefer not to say">Prefer not to say</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Currently taking medications for any of the following?"
          name="currentMedications"
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Checkbox value="blood pressure">Blood Pressure</Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="cholesterol">Cholesterol</Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="diabetes">Diabetes</Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="heart disease">Heart Disease</Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="other">Other</Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="none">None</Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="prefer not to say">Prefer not to say</Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Form.Item>

        <Form.Item
          label="How often do you engage in physical activity?"
          name="physicalActivityFrequency"
        >
          <Radio.Group>
            <Radio value="daily">Daily</Radio>
            <Radio value="a few times a week">A few times a week</Radio>
            <Radio value="rarely">Rarely</Radio>
            <Radio value="never">Never</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Which of the following do you currently have access to? (Check all that apply)"
          name="resourceAccess"
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Checkbox value="healthy affordable food">Healthy, affordable food</Checkbox>
              </Col>
              <Col xs={24} sm={12}>
                <Checkbox value="reliable transportation">Reliable transportation</Checkbox>
              </Col>
              <Col xs={24} sm={12}>
                <Checkbox value="health insurance">Health insurance</Checkbox>
              </Col>
              <Col xs={24} sm={12}>
                <Checkbox value="internet access at home">Internet access at home</Checkbox>
              </Col>
              <Col xs={24} sm={12}>
                <Checkbox value="a primary care doctor">A primary care doctor</Checkbox>
              </Col>
              <Col xs={24} sm={12}>
                <Checkbox value="none of the above">None of the above</Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Form.Item>
      </FormSection>

        <Divider>
          <DividerText>Consents & Releases</DividerText>
        </Divider>

      <FormSection>
        <Form.Item
          label="Media Release"
          name="mediaRelease"
        >
          <Radio.Group>
            <Radio value="yes">I acknowledge</Radio>
            <Radio value="no">I do not consent</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={
            <span>
              BHA Waiver Consent{' '}
              <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                (I accept the Terms and Conditions)
              </span>
            </span>
          }
          name="bhaWaiverConsent"
          valuePropName="checked"
        >
          <Checkbox>Accepted</Checkbox>
        </Form.Item>

        <Form.Item
          label="Transactional SMS Consent"
          name="transactionalSmsConsent"
          valuePropName="checked"
        >
          <Checkbox>
            By checking, you agree to receive transactional/informational SMS communications regarding appointment confirmations, reminders, scheduling updates, and customer care from Black Heart Association. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out. Reply HELP for help.
          </Checkbox>
        </Form.Item>

        <Form.Item
          label="Marketing SMS Consent"
          name="marketingSmsConsent"
          valuePropName="checked"
        >
          <Checkbox>
            By checking, you agree to receive promotional/marketing SMS communications from Black Heart Association. Msg frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to opt out. See our Privacy Policy and Terms for details.
          </Checkbox>
        </Form.Item>

        <Form.Item
          label="AI Voice Call Consent"
          name="aiVoiceCallConsent"
          valuePropName="checked"
        >
          <Checkbox>{AI_VOICE_CALL_CONSENT_TEXT}</Checkbox>
        </Form.Item>

        <div style={{ marginBottom: '16px', fontSize: '12px', color: '#666' }}>
          Privacy Policy: <a href="https://blackheartassociation.org/privacy-policy" target="_blank" rel="noopener noreferrer">/privacy</a> | Terms of Service: <a href="https://blackheartassociation.org/terms-of-service" target="_blank" rel="noopener noreferrer">/terms</a>
        </div>

        <Form.Item
          label={
            <span>
              Endless Health Acknowledgment{' '}
              <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                (Testing involves validated prick testing... I accept responsibility)
              </span>
            </span>
          }
          name="endlessHealthAcknowledgment"
        >
          <Radio.Group>
            <Radio value="True">True</Radio>
            <Radio value="False">False</Radio>
          </Radio.Group>
        </Form.Item>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <span>
                  Data Sharing Permission{' '}
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                    (Anonymized data for research)
                  </span>
                </span>
              }
              name="dataSharingPermission"
            >
              <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Radio value="Yes, I consent">Yes, I consent</Radio>
                <Radio value="No, I do not consent">No, I do not consent</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <span>
                  Heart Guard Champion Program{' '}
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                    (Advocacy program for health equity)
                  </span>
                </span>
              }
              name="heartGuardChampionOptIn"
            >
              <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Radio value="Sign me up!">Sign me up!</Radio>
                <Radio value="Not interested at this time">Not interested at this time</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

        <Divider>
          <DividerText>Optional Questions</DividerText>
        </Divider>

      <FormSection>
        <Form.Item
          label="Health Concern"
          name="healthConcern"
        >
          <StyledInput.TextArea 
            rows={3} 
            placeholder="Any health concerns..."
          />
        </Form.Item>

        <Form.Item
          label="Health Support"
          name="healthSupport"
        >
          <StyledInput.TextArea 
            rows={3} 
            placeholder="Health support needed..."
          />
        </Form.Item>
      </FormSection>
    </>
  );
};

export default PatientInfoTab;